import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, 
  Plus, 
  Edit2, 
  Trash2, 
  Copy,
  Save,
  Eye,
  FileSignature,
  Users,
  Building2,
  Briefcase,
  Search,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const ContractTemplatesPage = () => {
  const { theme } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // AI Generation state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiContractType, setAiContractType] = useState('client');
  const [generating, setGenerating] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'client', // client, team, affiliate, supplier
    content: '',
    variables: [],
    is_default: false,
    is_active: true
  });

  const defaultVariables = [
    '{{client_name}}', '{{client_company}}', '{{client_email}}', '{{client_address}}',
    '{{agency_name}}', '{{agency_address}}', '{{agency_phone}}', '{{agency_email}}',
    '{{project_name}}', '{{project_description}}', '{{project_amount}}',
    '{{contract_date}}', '{{start_date}}', '{{end_date}}',
    '{{hourly_rate}}', '{{total_hours}}', '{{payment_terms}}'
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/settings/contract-templates');
      setTemplates(res.data || getDemoTemplates());
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates(getDemoTemplates());
    } finally {
      setLoading(false);
    }
  };

  const getDemoTemplates = () => [
    {
      id: '1',
      name: 'Contrat Client Standard',
      description: 'Contrat de prestation de services pour les clients',
      type: 'client',
      content: `CONTRAT DE PRESTATION DE SERVICES

Entre les soussignés :

{{agency_name}}
Adresse : {{agency_address}}
Ci-après dénommé "Le Prestataire"

Et

{{client_company}}
Représenté par : {{client_name}}
Adresse : {{client_address}}
Ci-après dénommé "Le Client"

ARTICLE 1 - OBJET DU CONTRAT
Le présent contrat a pour objet la réalisation de la prestation suivante :
{{project_description}}

ARTICLE 2 - DURÉE
Le présent contrat prend effet à compter du {{start_date}} pour une durée de {{end_date}}.

ARTICLE 3 - PRIX ET MODALITÉS DE PAIEMENT
Le montant total de la prestation est fixé à {{project_amount}} € HT.
Taux horaire applicable : {{hourly_rate}} €/h
{{payment_terms}}

ARTICLE 4 - OBLIGATIONS DU PRESTATAIRE
Le Prestataire s'engage à réaliser la mission avec diligence et professionnalisme.

ARTICLE 5 - OBLIGATIONS DU CLIENT
Le Client s'engage à fournir tous les éléments nécessaires à la bonne exécution de la mission.

Fait à Paris, le {{contract_date}}
En deux exemplaires originaux.`,
      variables: ['client_name', 'client_company', 'client_address', 'agency_name', 'agency_address', 'project_description', 'start_date', 'end_date', 'project_amount', 'hourly_rate', 'payment_terms', 'contract_date'],
      is_default: true,
      is_active: true,
      created_at: '2026-01-01'
    },
    {
      id: '2',
      name: 'Contrat Freelance',
      description: 'Contrat pour les membres de l\'équipe freelance',
      type: 'team',
      content: `CONTRAT DE SOUS-TRAITANCE

Entre :
{{agency_name}} (Le Donneur d'ordre)
Et
{{client_name}} (Le Sous-traitant)

Date d'effet : {{start_date}}
Taux journalier : {{hourly_rate}} €

Conditions générales de collaboration...`,
      variables: ['agency_name', 'client_name', 'start_date', 'hourly_rate'],
      is_default: true,
      is_active: true,
      created_at: '2026-01-01'
    },
    {
      id: '3',
      name: 'Contrat Apporteur d\'affaires',
      description: 'Contrat de partenariat pour les apporteurs d\'affaires',
      type: 'affiliate',
      content: `CONTRAT D'APPORTEUR D'AFFAIRES

Entre :
{{agency_name}}
Et
{{client_name}}

Commission : 10% sur le premier projet
Durée : 12 mois renouvelable

Conditions...`,
      variables: ['agency_name', 'client_name'],
      is_default: false,
      is_active: true,
      created_at: '2026-01-01'
    }
  ];

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        variables: extractVariables(formData.content)
      };

      if (editingTemplate) {
        await api.put(`/settings/contract-templates/${editingTemplate.id}`, data);
        toast.success('Modèle modifié');
      } else {
        await api.post('/settings/contract-templates', data);
        toast.success('Modèle créé');
      }
      
      fetchTemplates();
      setShowModal(false);
      resetForm();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce modèle ?')) return;
    try {
      await api.delete(`/settings/contract-templates/${id}`);
      toast.success('Modèle supprimé');
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const data = {
        ...template,
        name: `${template.name} (copie)`,
        is_default: false
      };
      delete data.id;
      await api.post('/settings/contract-templates', data);
      toast.success('Modèle dupliqué');
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  // AI Template Generation
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Veuillez décrire le type de contrat souhaité');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await api.post('/settings/contract-templates/ai-generate', {
        prompt: aiPrompt,
        contract_type: aiContractType,
        include_variables: true
      });
      
      const generatedTemplate = response.data;
      
      // Pre-fill the form with generated content
      setFormData({
        name: generatedTemplate.name || `Modèle ${aiContractType} - Généré par IA`,
        description: generatedTemplate.description || aiPrompt,
        type: aiContractType,
        content: generatedTemplate.content,
        variables: extractVariables(generatedTemplate.content),
        is_default: false,
        is_active: false, // Draft by default - needs validation
        status: 'draft' // New status for AI generated templates
      });
      
      setShowAiModal(false);
      setShowModal(true);
      setAiPrompt('');
      toast.success('Modèle généré ! Veuillez le réviser et valider.');
    } catch (error) {
      console.error('Error generating template:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      type: template.type,
      content: template.content,
      variables: template.variables || [],
      is_default: template.is_default || false,
      is_active: template.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      type: 'client',
      content: '',
      variables: [],
      is_default: false,
      is_active: true
    });
  };

  const extractVariables = (content) => {
    const matches = content.match(/\{\{([^}]+)\}\}/g) || [];
    return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
  };

  const insertVariable = (variable) => {
    setFormData({
      ...formData,
      content: formData.content + `{{${variable}}}`
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      client: 'Client',
      team: 'Équipe',
      affiliate: 'Apporteur',
      supplier: 'Fournisseur'
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'client': return <Building2 className="w-4 h-4" />;
      case 'team': return <Users className="w-4 h-4" />;
      case 'affiliate': return <Briefcase className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      client: 'bg-blue-100 text-blue-700',
      team: 'bg-purple-100 text-purple-700',
      affiliate: 'bg-amber-100 text-amber-700',
      supplier: 'bg-emerald-100 text-emerald-700'
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  const filterTemplates = () => {
    let filtered = templates;
    
    if (searchQuery) {
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (activeTab !== 'all') {
      filtered = filtered.filter(t => t.type === activeTab);
    }

    return filtered;
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Modèles de Contrats
            </h1>
            <p className="text-sm text-[#64748b] mt-1">
              Créez et gérez vos modèles de contrats avec variables dynamiques
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowAiModal(true)}
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Générer avec IA
            </Button>
            <Button onClick={() => { resetForm(); setShowModal(true); }} style={{ backgroundColor: theme.colors?.accent }}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau modèle
            </Button>
          </div>
        </div>

        {/* Tabs & Search */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">Tous</TabsTrigger>
              <TabsTrigger value="client">Clients</TabsTrigger>
              <TabsTrigger value="team">Équipe</TabsTrigger>
              <TabsTrigger value="affiliate">Apporteurs</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
            <Input 
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filterTemplates().map((template) => (
            <Card key={template.id} className={cn("card", !template.is_active && "opacity-60")}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={getTypeColor(template.type)}>
                      {getTypeIcon(template.type)}
                      <span className="ml-1">{getTypeLabel(template.type)}</span>
                    </Badge>
                    {template.is_default && (
                      <Badge variant="outline" className="text-xs">Par défaut</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setPreviewTemplate(template); setShowPreviewModal(true); }}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDuplicate(template)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <CardTitle className="text-base mt-2">{template.name}</CardTitle>
                {template.description && (
                  <CardDescription className="text-xs">{template.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {/* Variables Preview */}
                <div className="mb-3">
                  <p className="text-xs text-[#64748b] mb-1">Variables utilisées</p>
                  <div className="flex flex-wrap gap-1">
                    {(template.variables || []).slice(0, 5).map((v, idx) => (
                      <Badge key={idx} variant="secondary" className="text-[10px]">{v}</Badge>
                    ))}
                    {(template.variables || []).length > 5 && (
                      <Badge variant="secondary" className="text-[10px]">+{template.variables.length - 5}</Badge>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal(template)}>
                    <Edit2 className="w-3 h-3 mr-1" />
                    Modifier
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(template.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filterTemplates().length === 0 && !loading && (
          <Card className="card p-12 text-center">
            <FileSignature className="w-12 h-12 mx-auto text-[#64748b] mb-4" />
            <h3 className="font-semibold mb-2">Aucun modèle de contrat</h3>
            <p className="text-sm text-[#64748b] mb-4">Créez votre premier modèle pour générer des contrats automatiquement</p>
            <Button onClick={() => setShowModal(true)} style={{ backgroundColor: theme.colors?.accent }}>
              <Plus className="w-4 h-4 mr-2" />
              Créer un modèle
            </Button>
          </Card>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Modifier le modèle' : 'Nouveau modèle de contrat'}</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-3 gap-4 py-4">
            <div className="col-span-2 space-y-4">
              <div>
                <Label>Nom du modèle</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ex: Contrat Client Standard..."
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Description courte du modèle..."
                />
              </div>

              <div>
                <Label>Type de contrat</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Contrat Client</SelectItem>
                    <SelectItem value="team">Contrat Équipe / Freelance</SelectItem>
                    <SelectItem value="affiliate">Contrat Apporteur d&apos;affaires</SelectItem>
                    <SelectItem value="supplier">Contrat Fournisseur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Contenu du contrat</Label>
                <Textarea 
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Rédigez votre contrat ici. Utilisez les variables {{variable}} pour les données dynamiques..."
                  rows={15}
                  className="font-mono text-sm"
                />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.is_default}
                    onCheckedChange={(v) => setFormData({...formData, is_default: v})}
                  />
                  <Label>Modèle par défaut</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                  />
                  <Label>Actif</Label>
                </div>
              </div>
            </div>

            {/* Variables Panel */}
            <div className="border-l pl-4">
              <Label className="mb-2 block">Variables disponibles</Label>
              <p className="text-xs text-[#64748b] mb-3">Cliquez pour insérer une variable</p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {defaultVariables.map((v, idx) => (
                  <button
                    key={idx}
                    onClick={() => insertVariable(v.replace(/\{\{|\}\}/g, ''))}
                    className="w-full text-left px-2 py-1.5 text-xs font-mono bg-slate-100 hover:bg-slate-200 rounded transition-colors"
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button onClick={handleSubmit} style={{ backgroundColor: theme.colors?.accent }}>
              <Save className="w-4 h-4 mr-2" />
              {editingTemplate ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu: {previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          
          {previewTemplate && (
            <div className="py-4">
              <pre className="whitespace-pre-wrap font-mono text-sm bg-slate-50 p-4 rounded-lg border">
                {previewTemplate.content}
              </pre>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>Fermer</Button>
            <Button onClick={() => { setShowPreviewModal(false); openEditModal(previewTemplate); }} style={{ backgroundColor: theme.colors?.accent }}>
              <Edit2 className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generation Modal */}
      <Dialog open={showAiModal} onOpenChange={setShowAiModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Générer un modèle avec IA
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-start gap-3">
                <Wand2 className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-purple-800">
                    L&apos;IA va créer un modèle professionnel
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    Le modèle inclura automatiquement les balises dynamiques (ex: {`{{client_name}}`}, {`{{project_amount}}`}) que vous pourrez personnaliser.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Type de contrat</Label>
              <Select value={aiContractType} onValueChange={setAiContractType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Contrat Client</SelectItem>
                  <SelectItem value="team">Contrat Équipe / Freelance</SelectItem>
                  <SelectItem value="affiliate">Contrat Apporteur d&apos;affaires</SelectItem>
                  <SelectItem value="supplier">Contrat Fournisseur / Partenaire</SelectItem>
                  <SelectItem value="nda">Accord de Confidentialité (NDA)</SelectItem>
                  <SelectItem value="maintenance">Contrat de Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Décrivez le contrat souhaité</Label>
              <Textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Ex: Un contrat de prestation de services pour développement web, incluant les clauses de propriété intellectuelle, les délais de paiement à 30 jours, et une clause de confidentialité..."
                rows={4}
              />
              <p className="text-xs text-slate-500">
                Plus vous êtes précis, meilleur sera le résultat. Mentionnez les clauses importantes.
              </p>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <p className="text-xs text-amber-700">
                  Le modèle généré sera en statut &quot;Brouillon&quot;. Vous devrez le réviser et l&apos;activer manuellement.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAiModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleAiGenerate}
              disabled={generating || !aiPrompt.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer le modèle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default ContractTemplatesPage;
